import { I18n } from '@calendly/i18n'
import { isEmpty, isEqual } from 'lodash-es'
import { createSelector, createSelectorCreator, defaultMemoize } from 'reselect'

import { getUser } from 'common'
import { nonWebConferencingKinds } from 'components/event_type_edit/location/location_field_with_buttons/constants'
import { idSelector, timeNotationSelector } from 'selectors/current_user'

import { BOOKING_METHODS, DATE_FORMAT } from './constants'
import {
  getAvailableSpots,
  getBackgroundEvents,
  getCalendarEvents,
  getCurrentTimeEvent,
  getUnavailabilityIntervals
} from './helpers'
import { calculateDurationInMinutes, getSelectedMemberEvents } from './utils'

// Create stable reference for filtered kinds
const POLL_NON_WEB_CONFERENCING_KINDS = nonWebConferencingKinds.filter((kind) => kind !== 'ask_invitee')

const bookingMethodSelector = ({ oneOffModal }) => oneOffModal.bookingMethod

const nonWebConferencingKindsSelector = createSelector(
  bookingMethodSelector,
  (bookingMethod) => (bookingMethod === BOOKING_METHODS.poll
    ? POLL_NON_WEB_CONFERENCING_KINDS
    : nonWebConferencingKinds)
)
const editingSelector = ({ oneOffModal }) => oneOffModal.editing
const currentTabSelector = ({ oneOffModal }) => oneOffModal.currentTab
const currentTimeSelector = ({ oneOffModal }) => oneOffModal.currentTime
const modalVisibleSelector = ({ oneOffModal }) => oneOffModal.modalVisible
const modalDirtySelector = ({ oneOffModal }) => oneOffModal.modalDirty
const modalTransitionedSelector = ({ oneOffModal }) => oneOffModal.modalTransitioned
const detailsStepSelector = ({ oneOffModal }) => oneOffModal.detailsStep
const availabilityStepSelector = ({ oneOffModal }) => oneOffModal.availabilityStep
const submitErrorSelector = ({ oneOffModal }) => oneOffModal.submitError
const isLoadingSelector = ({ oneOffModal }) => oneOffModal.loading
const eventTypeIdSelector = ({ oneOffModal }) => oneOffModal.eventTypeId
const isSelectedSpotsDropdownOpenSelector = ({ oneOffModal }) => oneOffModal.isSelectedSpotsDropdownOpen

const detailsStepFieldsSelector = createSelector(detailsStepSelector, ({ fields }) => fields)
const mobileCurrentViewSelector = createSelector(availabilityStepSelector, ({ mobileCurrentView }) => mobileCurrentView)
const selectedSpotsSelector = createSelector(
  availabilityStepSelector,
  ({ selectedSpots }) => (selectedSpots)
)

const reserveTimesSelector = createSelector(availabilityStepSelector, ({ reserveTimes }) => reserveTimes)

const notifyInviteesSelector = createSelector(detailsStepSelector, ({ notifyInvitees }) => notifyInvitees)
const votesVisibleSelector = createSelector(detailsStepSelector, ({ votesVisible }) => votesVisible)

const timezoneSelector = createSelector(
  availabilityStepSelector,
  getUser.select(),
  ({ timezone }, { data: currentUser }) => timezone || currentUser.timezone
)

const groupedSelectedSpotsSelector = createSelector(
  timezoneSelector,
  selectedSpotsSelector,
  (timezone, selectedSpots) => {
    const groupedSpots = {}
    selectedSpots.forEach((spot) => {
      const date = spot.start.clone().tz(timezone).format(DATE_FORMAT)
      groupedSpots[date] = groupedSpots[date] || []
      groupedSpots[date].push(spot.start)
    })
    return groupedSpots
  }
)

const durationFieldSelector = createSelector(availabilityStepSelector, ({ durationField }) => durationField)
const loadingCalendarDataSelector = createSelector(availabilityStepSelector, ({ loadingCalendarData }) => loadingCalendarData)

const eventTypeDurationSelector = createSelector(
  durationFieldSelector,
  calculateDurationInMinutes
)

const eventTypeDurationFormattedSelector = createSelector(durationFieldSelector,
  ({ duration, customDurationValue, customDurationUnit }) => {
    const durationValue = customDurationValue || duration
    const localizedUnit = customDurationValue && customDurationUnit === 'hours'
      ? I18n.t('time.hours_abbr', { count: durationValue })
      : I18n.t('time.min_abbr', { count: durationValue })

    return `${durationValue} ${localizedUnit}`
  })

const fetchedEventsDaysRangeSelector = createSelector(
  availabilityStepSelector,
  ({ fetchedEventsDaysRange }) => fetchedEventsDaysRange
)

const hostsFieldSelector = createSelector(availabilityStepSelector, ({ hostsField }) => hostsField)
const filteredSuggestionsSelector = createSelector(hostsFieldSelector, ({ suggestions, selectedMembers }) => (
  suggestions.filter(({ email }) => !selectedMembers.some(({ email: selectedEmail }) => email === selectedEmail))
))
const suggestionsIsLoadingSelector = createSelector(hostsFieldSelector, ({ isLoading }) => isLoading)
const selectedMembersSelector = createSelector(hostsFieldSelector, ({ selectedMembers }) => selectedMembers)

const colorIndexesSelector = createSelector(hostsFieldSelector, ({ colorIndexes }) => colorIndexes)

const selectedMembersEvents = createSelector(
  selectedMembersSelector,
  idSelector,
  getSelectedMemberEvents
)

const currentTimeEventSelector = createSelector(
  currentTimeSelector,
  getCurrentTimeEvent
)

const calendarEventsSelector = createSelector(
  selectedMembersEvents,
  timezoneSelector,
  timeNotationSelector,
  currentTimeSelector,
  getCalendarEvents
)

const hostsScheduleSelector = createSelector(
  hostsFieldSelector,
  ({ selectedMembers }) => selectedMembers.map((member) => {
    const selectedRuleId = member.selected_rule_id || member.primary_availability_rule_id
    if (member.availabilityRules) {
      return member.availabilityRules.find((rule) => rule.id === selectedRuleId)
    }

    return { id: member.primary_availability_rule_id, rules: [] }
  })
)

const datesRangeSelector = createSelector(
  availabilityStepSelector,
  ({ rangeStart, rangeEnd }) => ({ rangeStart, rangeEnd })
)

const fetchedEventsStartRangeSelector = createSelector(
  availabilityStepSelector,
  ({ fetchedEventsStartRange }) => (fetchedEventsStartRange)
)

const selectingSpotsEnabledSelector = createSelector(
  availabilityStepSelector,
  ({ selectingSpotsEnabled }) => (selectingSpotsEnabled)
)

const createDeepEqualSelector = createSelectorCreator(
  defaultMemoize,
  isEqual
)

const unavailabilityIntervalsSelector = createDeepEqualSelector(
  hostsScheduleSelector,
  timezoneSelector,
  datesRangeSelector,
  fetchedEventsDaysRangeSelector,
  getUnavailabilityIntervals
)

const availableSpotsSelector = createSelector(
  unavailabilityIntervalsSelector,
  timezoneSelector,
  datesRangeSelector,
  timeNotationSelector,
  eventTypeDurationSelector,
  selectedSpotsSelector,
  selectedMembersEvents,
  fetchedEventsDaysRangeSelector,
  selectingSpotsEnabledSelector,
  () => true, // allowEventsOverlap
  () => true, // allowSpotsOverlap
  () => true, // allowInternalEventsOverlap
  getAvailableSpots
)

const backgroundEventsSelector = createSelector(
  unavailabilityIntervalsSelector,
  timezoneSelector,
  datesRangeSelector,
  timeNotationSelector,
  selectedSpotsSelector,
  fetchedEventsDaysRangeSelector,
  reserveTimesSelector,
  selectingSpotsEnabledSelector,
  calendarEventsSelector,
  getBackgroundEvents
)

const hasVotesSelector = createSelector(
  backgroundEventsSelector,
  (backgroundEvents) => backgroundEvents.some((evt) => !isEmpty(evt.extendedProps.invitees))
)

const formDataSelector = createSelector(
  durationFieldSelector,
  selectedSpotsSelector,
  timezoneSelector,
  selectedMembersSelector,
  reserveTimesSelector,
  detailsStepSelector,
  bookingMethodSelector,
  (durationField, selectedSpots, timezone, selectedMembers, reserveTimes, { votesVisible, notifyInvitees }, bookingMethod) => ({
    durationField, selectedSpots, timezone, selectedMembers, reserveTimes, votesVisible, notifyInvitees, bookingMethod
  })
)

export {
  eventTypeIdSelector,
  timezoneSelector,
  bookingMethodSelector,
  nonWebConferencingKindsSelector,
  editingSelector,
  isLoadingSelector,
  currentTabSelector,
  colorIndexesSelector,
  modalDirtySelector,
  modalVisibleSelector,
  modalTransitionedSelector,
  currentTimeEventSelector,
  eventTypeDurationSelector,
  detailsStepFieldsSelector,
  mobileCurrentViewSelector,
  submitErrorSelector,
  formDataSelector,
  hasVotesSelector,
  eventTypeDurationFormattedSelector,
  durationFieldSelector,
  filteredSuggestionsSelector,
  suggestionsIsLoadingSelector,
  selectedMembersEvents,
  availableSpotsSelector,
  selectedMembersSelector,
  selectedSpotsSelector,
  groupedSelectedSpotsSelector,
  calendarEventsSelector,
  backgroundEventsSelector,
  fetchedEventsStartRangeSelector,
  datesRangeSelector,
  fetchedEventsDaysRangeSelector,
  reserveTimesSelector,
  votesVisibleSelector,
  notifyInviteesSelector,
  availabilityStepSelector,
  loadingCalendarDataSelector,
  isSelectedSpotsDropdownOpenSelector
}
